import React from "react";
import { Checkbox, Space, Tooltip, Spin } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { LoadingOutlined } from "@ant-design/icons";
import { useTranslate } from "@refinedev/core";

interface BooleanColumnHeaderProps {
  /** Column title text */
  title: string;
  /** Whether all items are checked */
  allChecked: boolean;
  /** Whether some (but not all) items are checked */
  someChecked: boolean;
  /** Whether the column is read-only */
  disabled?: boolean;
  /** Whether a bulk operation is in progress */
  loading?: boolean;
  /** Callback for check all / uncheck all */
  onToggleAll: (checked: boolean) => void;
}

/**
 * Column header with check all/uncheck all checkbox for boolean columns.
 */
export const BooleanColumnHeader: React.FC<BooleanColumnHeaderProps> = ({
  title,
  allChecked,
  someChecked,
  disabled = false,
  loading = false,
  onToggleAll,
}) => {
  const translate = useTranslate();

  const tooltipText = allChecked
    ? translate("table.uncheckAll", "Uncheck all")
    : translate("table.checkAll", "Check all");

  const handleChange = (e: CheckboxChangeEvent) => {
    if (!loading && !disabled) {
      onToggleAll(e.target.checked);
    }
  };

  return (
    <Space size={8}>
      <span>{title}</span>
      {!disabled && (
        <Tooltip title={tooltipText}>
          {loading ? (
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 12 }} spin />}
              size="small"
            />
          ) : (
            <Checkbox
              checked={allChecked}
              indeterminate={someChecked && !allChecked}
              onChange={handleChange}
              onClick={(e) => e.stopPropagation()}
              aria-label={tooltipText}
            />
          )}
        </Tooltip>
      )}
    </Space>
  );
};

export default BooleanColumnHeader;
